/**
 * 报价卡片的纯数据整理 (移植自旧版 index.html formatQuoteMessage / getProductTypeName):
 *  - calculation_details 按 query_index 分组成「询价 N」, 每组带小计
 *  - 产品类型 key → 中文名 (kt / poster / car / uv)
 *  - 立体字 (有 longest_side_cm) 数量单位为「个」, 其余为「张」
 *  - extracted_params 整理成「识别参数」块
 *  - 报价单 → 纯文本 (「复制报价」写入剪贴板)
 */

export interface CalcDetail {
  product_name?: string;
  area?: number | null;
  longest_side_cm?: number | null;
  thickness_spec?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  total_price?: number | null;
  applied_rule?: string | null;
  double_sided?: boolean | null;
  double_sided_multiplier?: number | null;
  calculation_steps?: string[] | null;
  query_index?: number | null;
}

export interface ExtractedParam {
  product_type?: string | null;
  width?: number | null;
  height?: number | null;
  quantity?: number | null;
  edge_length?: number | null;
  cutting_required?: boolean | null;
  double_sided?: boolean | null;
  longest_side_cm?: number | null;
  thickness_spec?: string | null;
  query_index?: number | null;
}

/** 一轮报价完成时留存的快照 (挂在该轮最后一条 assistant 消息上) */
export interface QuoteSnapshot {
  details: CalcDetail[];
  totalPrice: number;
  extractedParams: ExtractedParam[];
}

const PRODUCT_TYPE_NAMES: Record<string, string> = {
  kt: "KT板",
  poster: "海报/画面",
  car: "车贴",
  uv: "UV画面/PVC板",
};

/** 旧版 getProductTypeName: 已知 key 转中文, 其余 (已是产品名) 原样返回 */
export function productTypeName(type: string | null | undefined): string {
  if (typeof type === "string" && type) return PRODUCT_TYPE_NAMES[type] ?? type;
  return "未知产品";
}

/** 立体字按「个」计, 平面产品按「张」计 */
export function quantityUnit(item: { longest_side_cm?: number | null }): "个" | "张" {
  return item.longest_side_cm ? "个" : "张";
}

export interface QueryGroup<T> {
  /** null 表示响应里没有 query_index (旧格式), 不显示「询价 N」标题 */
  queryIndex: number | null;
  items: T[];
}

/** 按 query_index 分组, 保持首次出现的顺序; 首项没有 query_index 时整体视为一组 (同旧版判断) */
export function groupByQuery<T extends { query_index?: number | null }>(items: T[]): QueryGroup<T>[] {
  if (!items || items.length === 0) return [];
  if (items[0].query_index === undefined || items[0].query_index === null) {
    return [{ queryIndex: null, items: [...items] }];
  }
  const groups = new Map<number, T[]>();
  for (const it of items) {
    const qi = typeof it.query_index === "number" ? it.query_index : 0;
    const arr = groups.get(qi);
    if (arr) arr.push(it);
    else groups.set(qi, [it]);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([queryIndex, groupItems]) => ({ queryIndex, items: groupItems }));
}

export function groupSubtotal(items: CalcDetail[]): number {
  return items.reduce((sum, d) => sum + (Number(d.total_price) || 0), 0);
}

/** 把 smart-quote 响应整理成报价快照; 不是完整报价 (追问 / 失败 / 无明细) 时返回 null */
export function quoteSnapshotFrom(resp: {
  success: boolean;
  needs_clarification?: boolean;
  calculation_details?: Array<Record<string, unknown>> | null;
  extracted_params?: Array<Record<string, unknown>> | null;
  total_price?: number | null;
}): QuoteSnapshot | null {
  if (!resp.success || resp.needs_clarification) return null;
  const details = (resp.calculation_details ?? []) as CalcDetail[];
  if (details.length === 0) return null;
  return {
    details,
    totalPrice: Number(resp.total_price) || groupSubtotal(details),
    extractedParams: (resp.extracted_params ?? []) as ExtractedParam[],
  };
}

/** 服务端留存的报价快照 (SessionTurn.quote) → 卡片快照; 没有 / 无明细返回 null */
export function quoteSnapshotFromStored(q: {
  calculation_details?: Array<Record<string, unknown>> | null;
  extracted_params?: Array<Record<string, unknown>> | null;
  total_price?: number | null;
} | null | undefined): QuoteSnapshot | null {
  if (!q) return null;
  return quoteSnapshotFrom({ success: true, ...q });
}

export function fmtMoney(n: number | null | undefined, digits = 2): string {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return "-";
  return Number(n).toFixed(digits);
}

/** 明细行的规格说明: 立体字 = 最长边 + 厚度; 平面 = 面积 */
export function detailSpec(d: CalcDetail): string {
  const parts: string[] = [];
  if (d.longest_side_cm) parts.push(`最长边 ${d.longest_side_cm} cm`);
  if (d.thickness_spec) parts.push(`厚度 ${d.thickness_spec}`);
  if (!d.longest_side_cm && d.area != null) parts.push(`面积 ${fmtMoney(d.area)} m²`);
  return parts.join(" · ");
}

/** 识别参数一行的说明 (尺寸 / 数量 / 包边 / 裁切 / 双面) */
export function paramSpec(p: ExtractedParam): string[] {
  const parts: string[] = [];
  if (p.longest_side_cm) {
    parts.push(`最长边 ${p.longest_side_cm} cm`);
    if (p.thickness_spec) parts.push(`厚度 ${p.thickness_spec}`);
  } else {
    parts.push(`尺寸 ${p.width || 0}米 × ${p.height || 0}米`);
  }
  parts.push(`数量 ${p.quantity || 0} ${quantityUnit(p)}`);
  if (p.edge_length) parts.push(`包边 ${p.edge_length}米`);
  if (p.cutting_required) parts.push("裁切: 需要");
  if (p.double_sided) parts.push("双面: 是");
  return parts;
}

/**
 * 报价单 → 纯文本 (复制给客户 / 贴到微信):
 *   报价单
 *   【询价 1】
 *   1. KT板 (面积 0.72 m²) 50 张 × ¥6.50 = ¥325.00
 *   询价 1 小计: ¥385.00
 *   合计: ¥486.00
 */
export function quoteToPlainText(q: Pick<QuoteSnapshot, "details" | "totalPrice">, title = "报价单"): string {
  const lines: string[] = [title];
  const groups = groupByQuery(q.details ?? []);
  const multi = groups.length > 1;
  for (const g of groups) {
    if (g.queryIndex !== null && multi) lines.push("", `【询价 ${g.queryIndex + 1}】`);
    g.items.forEach((d, i) => {
      const spec = detailSpec(d);
      const double = d.double_sided ? ` 双面×${fmtMoney(d.double_sided_multiplier, 1)}` : "";
      lines.push(
        `${i + 1}. ${productTypeName(d.product_name)}${spec ? ` (${spec})` : ""}${double} ` +
          `${d.quantity || 0} ${quantityUnit(d)} × ¥${fmtMoney(d.unit_price ?? 0)} = ¥${fmtMoney(d.total_price ?? 0)}`,
      );
    });
    if (g.queryIndex !== null && multi) lines.push(`询价 ${g.queryIndex + 1} 小计: ¥${fmtMoney(groupSubtotal(g.items))}`);
  }
  lines.push("", `合计: ¥${fmtMoney(q.totalPrice)}`);
  return lines.join("\n");
}
